import { ArrowUpRight } from 'lucide-react'
import { useState, useEffect } from 'react'
import { TrendingAssetsBaseProps, getTypeColor } from './TrendingAssetsBase'
import { ProjectLogo } from './ProjectLogo'
import { formatCompactNumber } from '@/utils'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'

export const TrendingAssetsMobile = ({ assets, onAssetClick, loading }: TrendingAssetsBaseProps) => {
  const { scrollContainerRef, isDragging, handlers, style } = useHorizontalScroll()
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark'))
    }
    
    checkTheme()
    
    const observer = new MutationObserver(checkTheme)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })
    
    return () => observer.disconnect()
  }, [])

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 300,
        behavior: 'smooth'
      })
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 
            className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold" 
            
          >
            Trending Assets
          </h3>
          <button
            className="w-[52px] h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.08)] flex items-center justify-center transition-colors flex-shrink-0"
            aria-label="Scroll right"
            disabled
          >
            <ArrowUpRight className="w-4 h-4 text-[#2d2923] dark:text-[#fdfdfd]" />
          </button>
        </div>
        <div className="h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.08)] px-1.5 py-1 flex items-center justify-center">
          <span className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">Loading trending assets...</span>
        </div>
      </div>
    )
  }

  if (assets.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 
            className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold" 
            
          >
            Trending Assets
          </h3>
          <button
            className="w-[52px] h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.08)] flex items-center justify-center transition-colors flex-shrink-0"
            aria-label="Scroll right"
            disabled
          >
            <ArrowUpRight className="w-4 h-4 text-[#2d2923] dark:text-[#fdfdfd]" />
          </button>
        </div>
        <div className="h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.08)] px-1.5 py-1 flex items-center justify-center">
          <span className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">No trending assets available</span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header row: Title + Button */}
      <div className="flex items-center justify-between">
        <h3 
          className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold" 
          
        >
          Trending Assets
        </h3>
        <button
          onClick={scrollRight}
          className="w-[52px] h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.2)] hover:bg-[rgba(45,41,35,0.12)] dark:hover:bg-[rgba(253,253,253,0.3)] flex items-center justify-center transition-colors flex-shrink-0"
          aria-label="Scroll right"
        >
          <ArrowUpRight className="w-4 h-4 text-[#2d2923] dark:text-[#fdfdfd]" />
        </button>
      </div>
      
      {/* Assets container row */}
      <div className="w-full h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[rgba(253,253,253,0.08)] overflow-hidden relative">
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-hide h-full items-center absolute inset-0 gap-1.5 pl-1 pt-1 pb-1"
          style={style}
          {...handlers}
        >
          {assets.map((asset) => (
            <div
              key={asset.order_id}
              className="bg-white dark:bg-[#1c1c1c] rounded-full flex-shrink-0 cursor-pointer hover:shadow-md dark:hover:shadow-none dark:hover:bg-[rgba(253,253,253,0.1)] transition-shadow flex items-center gap-2.5 pl-2 pr-[18px] py-2"
              onClick={() => {
                // Prevent click if we were dragging
                if (!isDragging) {
                  onAssetClick?.(asset)
                }
              }}
            >
              <div className="flex items-start gap-2.5">
                <ProjectLogo logo={asset.logo} alt={asset.project_name} size="sm" />
                <div className="flex items-baseline gap-3">
                  <span 
                    className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-medium" 
                    
                  >
                    {asset.project_name}
                  </span>
                  <span 
                    className="font-plus-jakarta text-xs leading-4 font-semibold"
                    style={{ 
                      color: isDark 
                        ? '#fdfdfd' 
                        : getTypeColor(asset.deal_type) 
                    }}
                  >
                    {asset.deal_type}
                  </span>
                </div>
              </div>
              <div 
                className="text-sm leading-5 font-semibold text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-nowrap" 
                
              >
                ${formatCompactNumber(asset.offered_amount)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

